<template>
  <div class="knowledgeDetailsPage">
    <div class="titleBox">
      <div class="titleLeft" @click="goBack">
        <img src="../../assets/images/titleArrow.png" alt="" />
      </div>
      <div class="titleCentre">{{ pageTitle }}</div>
      <div class="titleRight"></div>
    </div>

    <div class="bannerBox">
      <img :src="knowledgeHomeImage" alt="" @click="handleKnowledgeHomeClick" />
    </div>

    <div class="swipeArea" @touchstart="onTouchStart" @touchend="onTouchEnd">
      <div
        class="contentCard"
        :class="{ noMedia: !showMediaSection }"
      >
      <audio
        ref="audioPlayer"
        :src="showMediaSection ? currentQuestion.audioUrl : ''"
        preload="metadata"
        @canplay="onAudioCanPlay"
        @loadedmetadata="onAudioLoadedMetadata"
        @timeupdate="onAudioTimeUpdate"
        @error="onAudioError"
        @ended="onAudioEnded"
      ></audio>

      <div class="questionHeader">
        <div class="questionTitle">{{ currentQuestion.title }}</div>
        <div class="questionCount">
          {{ currentIndex + 1 }}/{{ questionList.length }}
        </div>
      </div>

      <div v-if="showMediaSection" class="imageWrap">
        <img :src="currentQuestion.imageFront" alt="" />
      </div>

      <div v-if="showMediaSection" class="audioBox">
        <div class="timeRow">
          <span>{{ formatTime(currentQuestion.currentTime) }}</span>
          <div class="progressRange" ref="progressTrack" @click="onTrackClick">
            <div class="progressRangeBg"></div>
            <div
              class="progressRangeActive"
              :style="{ width: progressPercent + '%' }"
            ></div>
            <img
              src="../../assets/icons/circle.png"
              alt=""
              class="progressCircle"
              :style="{ left: `calc(${progressPercent}% - 9px)` }"
              @mousedown.stop.prevent="onDragStart"
              @touchstart.stop.prevent="onDragStart"
            />
          </div>
          <span>{{ formatTime(currentQuestion.duration) }}</span>
        </div>

        <div class="waveBox">
          <img src="../../assets/icons/rune.png" alt="" class="waveImg" />
          <img
            :src="isPlaying ? require('../../assets/icons/pause.png') : require('../../assets/icons/play.png')"
            alt=""
            class="playBtn"
            @click="togglePlay"
          />
        </div>
      </div>
      </div>

      <div class="tabBox" :class="{ fullTabs: dynamicTabs.length === 4 }">
        <div
          v-for="tab in dynamicTabs"
          :key="tab.key"
          class="tabItem"
          :class="{ active: activeTab === tab.key }"
          @click="activeTab = tab.key"
        >
          {{ tab.label }}
        </div>
      </div>

      <div v-if="activeTab === 'rating'" class="descCard ratingCard">
        <div
          v-for="line in currentRatingLines"
          :key="line.key"
          class="ratingLine"
          :class="[
            `level${line.level || 3}`,
            {
              spacer: line.type === 'spacer',
              maskableLine: isRatingLineMaskable(line),
              masked: isRatingLineMaskable(line) && !isRatingLineRevealed(line),
            },
          ]"
          @click="toggleRatingLine(line)"
        >
          <template v-if="line.type !== 'spacer'">
            <span v-if="!isRatingLineMaskable(line) || isRatingLineRevealed(line)">{{ line.text }}</span>
            <span v-else class="maskBlock"></span>
          </template>
        </div>
      </div>
      <div v-else-if="activeTab === 'videoTutorial'" class="descCard videoTutorialCard">
        <video
          v-if="currentQuestion.videoTutorialVideoUrl"
          class="videoTutorialPlayer"
          controls
          preload="metadata"
          :poster="currentQuestion.videoTutorialImageUrl"
          :src="currentQuestion.videoTutorialVideoUrl"
        ></video>
        <div v-else class="videoTutorialEmpty">暂无视频</div>
      </div>
      <div v-else class="descCard">{{ currentTabContent }}</div>
    </div>

    <div class="bottomBar">
      <button class="masteredBtn" @click="toggleMastered">
        {{ currentQuestion.isMastered ? "已掌握" : "未掌握" }}
      </button>
      <div class="actionList">
        <div class="actionItem" @click="toggleFavorite">
          <img
            :src="
              currentQuestion.isFavorite
                ? require('../../assets/icons/saved.png')
                : require('../../assets/icons/notSaved.png')
            "
            alt=""
          />
          <span>{{ currentQuestion.isFavorite ? "取消" : "收藏" }}</span>
        </div>
        <div class="actionItem" @click="openPointList">
          <img src="../../assets/icons/list.png" alt="" />
          <span>考点列表</span>
        </div>
      </div>
    </div>

    <div v-if="showPointList" class="pointMask" @click="closePointList">
      <div class="pointSheet" @click.stop>
        <div class="pointLegend">
          <div class="legendItem">
            <span class="dot mastered"></span>
            <span>已掌握</span>
          </div>
          <div class="legendItem">
            <span class="dot unMastered"></span>
            <span>未掌握</span>
          </div>
        </div>

        <div class="pointList">
          <div
            class="pointRow"
            v-for="(item, index) in questionList"
            :key="item.id || index"
            @click="selectPoint(index)"
          >
            <div class="leftInfo">
              <span
                class="dot"
                :class="{
                  mastered: isMastered(item, index),
                  unMastered: !isMastered(item, index),
                }"
              ></span>
              <span class="rowTitle">{{ index + 1 }}.{{ item.title }}</span>
            </div>
            <img
              src="../../assets/icons/right-arrow.png"
              alt=""
              class="rowArrow"
            />
          </div>
        </div>
      </div>
    </div>

    <transition name="action-toast-fade">
      <div v-if="actionToastVisible" class="actionToast">{{ actionToastText }}</div>
    </transition>
  </div>
</template>

<script>
import { knowledgeList, favoriteSave, learnRecordSave } from "@/api/knowledge/index.js";
import { getToken } from "@/utils/auth";

export default {
  data() {
    return {
      pageTitle: "肺部听诊",
      currentType: 1,
      currentIndex: 0,
      activeTab: "part",
      isPlaying: false,
      touchStartX: 0,
      isDraggingProgress: false,
      showPointList: false,
      isSubmittingFavorite: false,
      isSubmittingMastered: false,
      isSubmittingLearnRecord: false,
      ratingRevealMap: {},
      questionList: [],
      mediaHost: "",
      actionToastVisible: false,
      actionToastText: "",
      actionToastTimer: null,
      practiceTime: 0,
      practiceTimer: null,
    };
  },
  computed: {
    currentQuestion() {
      return (
        this.questionList[this.currentIndex] ||
        this.questionList[0] || {
          title: "",
          imageFront: require("../../assets/images/knowledge.png"),
          audioUrl: "",
          duration: 0,
          currentTime: 0,
          isFavorite: false,
          isMastered: false,
          part: "",
          mechanism: "",
          feature: "",
          disease: "",
        }
      );
    },
    progressPercent() {
      const duration = Number(this.currentQuestion.duration || 0);
      const currentTime = Number(this.currentQuestion.currentTime || 0);
      if (!duration) {
        return 0;
      }
      return Math.min(100, Math.max(0, (currentTime / duration) * 100));
    },
    dynamicTabs() {
      const tabMap = {
        1: [
          { key: "part", label: "听诊部位" },
          { key: "mechanism", label: "发生机制" },
          { key: "feature", label: "听诊特点" },
          { key: "disease", label: "常见疾病" },
        ],
        2: [{ key: "formula", label: "诊断公式" }],
        3: [
          { key: "ecgFeature", label: "心电图特点" },
          { key: "mnemonic", label: "速记口诀" },
        ],
        4: [
          { key: "briefHistory", label: "简要病史" },
          { key: "rating", label: "评分标准" },
          { key: "videoTutorial", label: "视频带教" },
        ],
        5: [
          { key: "briefHistory", label: "简要病史" },
          { key: "rating", label: "评分标准" },
          { key: "videoTutorial", label: "视频带教" },
        ],
      };
      return tabMap[this.currentType] || tabMap[1];
    },
    showMediaSection() {
      return ![4, 5].includes(Number(this.currentType));
    },
    currentRatingLines() {
      if (!this.currentQuestion || !Array.isArray(this.currentQuestion.ratingLines)) {
        return [];
      }
      return this.currentQuestion.ratingLines;
    },
    currentTabContent() {
      return this.currentQuestion[this.activeTab] || "";
    },
    knowledgeHomeImage() {
      const configImage = this.$store.getters.knowledgePointImageUrl;
      return (
        this.formatKnowledgeImageUrl(configImage) ||
        require("../../assets/icons/knowledgHome.png")
      );
    },
    knowledgePointJumpUrl() {
      return this.$store.getters.knowledgePointJumpUrl || "";
    },
  },
  mounted() {
    window.addEventListener("mousemove", this.onDragMove);
    window.addEventListener("mouseup", this.onDragEnd);
    window.addEventListener("touchmove", this.onDragMove, { passive: false });
    window.addEventListener("touchend", this.onDragEnd);
  },
  beforeDestroy() {
    this.stopPracticeTimer();
    this.pauseAudio(false);
    if (this.actionToastTimer) {
      window.clearTimeout(this.actionToastTimer);
      this.actionToastTimer = null;
    }
    window.removeEventListener("mousemove", this.onDragMove);
    window.removeEventListener("mouseup", this.onDragEnd);
    window.removeEventListener("touchmove", this.onDragMove);
    window.removeEventListener("touchend", this.onDragEnd);
  },
  created() {
    this.initFromRoute();
    this.getKnowledgeListData();
  },
  methods: {
    getBannerPrefixFromRoute() {
      return String(this.imgUrl || `${window.location.origin}/file/`).replace(
        /\/+$/,
        ""
      );
    },
    formatKnowledgeImageUrl(url) {
      if (!url) {
        return "";
      }
      if (/^(https?:)?\/\//i.test(url)) {
        return encodeURI(url);
      }
      const imgPrefix = String(this.imgUrl || `${window.location.origin}/file/`).replace(
        /\/+$/,
        ""
      );
      const normalizedPath = String(url).startsWith("/")
        ? String(url)
        : `/${String(url)}`;
      if (/\/file$/i.test(imgPrefix) && normalizedPath.startsWith("/file/")) {
        return encodeURI(`${imgPrefix}${normalizedPath.replace(/^\/file/, "")}`);
      }
      return encodeURI(`${imgPrefix}${normalizedPath}`);
    },
    getMediaHostFromRoute() {
      return String(this.imgUrl || `${window.location.origin}/file/`).replace(
        /\/+$/,
        ""
      );
    },
    parseTokenPayload() {
      const token = getToken();
      if (!token || token.indexOf(".") === -1) {
        return {};
      }
      try {
        const payload = token
          .split(".")[1]
          .replace(/-/g, "+")
          .replace(/_/g, "/");
        const decoded = decodeURIComponent(
          atob(payload)
            .split("")
            .map(
              (char) => `%${`00${char.charCodeAt(0).toString(16)}`.slice(-2)}`
            )
            .join("")
        );
        return JSON.parse(decoded);
      } catch (error) {
        return {};
      }
    },
    getGlobalUserId() {
      const payload = this.parseTokenPayload();
      return payload.user_id || payload.userId || null;
    },
    getCodeFromRoute() {
      const paramsCode = this.toValidNumber(this.$route.params.code);
      const queryCode = this.toValidNumber(this.$route.query.code);
      const pathParts = (this.$route.path || "").split("/").filter(Boolean);
      const pathCode = this.toValidNumber(pathParts[pathParts.length - 2]);
      return paramsCode ?? queryCode ?? pathCode ?? 1;
    },
    toValidNumber(value) {
      if (value === undefined || value === null || value === "") {
        return null;
      }
      const numberValue = Number(value);
      return Number.isNaN(numberValue) ? null : numberValue;
    },
    getCategoryIdFromRoute() {
      const paramsCategoryId = this.toValidString(
        this.$route.params.categoryId
      );
      const queryCategoryId = this.toValidString(this.$route.query.categoryId);
      const pathParts = (this.$route.path || "").split("/").filter(Boolean);
      const pathCategoryId = this.toValidString(
        pathParts[pathParts.length - 1]
      );
      return paramsCategoryId ?? queryCategoryId ?? pathCategoryId;
    },
    getMedicalKnowledgeIdFromRoute() {
      const paramsId = this.toValidString(this.$route.params.medicalKnowledgeId);
      const queryId = this.toValidString(this.$route.query.medicalKnowledgeId);
      const pathParts = (this.$route.path || "").split("/").filter(Boolean);
      const pathId = this.toValidString(pathParts[pathParts.length - 1]);
      return paramsId ?? queryId ?? pathId;
    },
    toValidString(value) {
      if (value === undefined || value === null || value === "") {
        return null;
      }
      return String(value);
    },
    parseMaybeJsonObject(value) {
      if (!value) {
        return null;
      }
      if (typeof value === "object") {
        return value;
      }
      if (typeof value !== "string") {
        return null;
      }
      try {
        return JSON.parse(value);
      } catch (error) {
        return null;
      }
    },
    normalizeLineText(text) {
      if (text === undefined || text === null) {
        return "";
      }
      return String(text).replace(/\s+/g, " ").trim();
    },
    buildRatingLines(ratingCriteria) {
      const source = this.parseMaybeJsonObject(ratingCriteria);
      if (source && Array.isArray(source.treeNodes) && source.treeNodes.length) {
        const lines = [];
        source.treeNodes.forEach((level1, level1Index) => {
          const level1Title = this.normalizeLineText(
            level1.primaryCategory || level1.secondaryCategory || level1.ratingItem
          );
          const level2List = Array.isArray(level1.children) ? level1.children : [];
          if (level1Title) {
            lines.push({
              key: `l1-${level1.seq || level1.ratingCriteriaDetailId || level1Index}`,
              level: 1,
              text: level1Title,
              maskable: level2List.length === 0,
            });
          }

          level2List.forEach((level2, level2Index) => {
            const level3List = Array.isArray(level2.children) ? level2.children : [];
            const level2Title = this.normalizeLineText(
              level2.secondaryCategory || level2.primaryCategory || level2.ratingItem
            );
            if (level2Title) {
              lines.push({
                key: `l2-${level2.seq || level2.ratingCriteriaDetailId || `${level1Index}-${level2Index}`}`,
                level: 2,
                text: level2Title,
                maskable: level3List.length === 0,
              });
            }

            level3List.forEach((level3, level3Index) => {
              const level3Text = this.normalizeLineText(level3.ratingItem);
              if (level3Text) {
                lines.push({
                  key: `l3-${level3.seq || level3.ratingCriteriaDetailId || `${level1Index}-${level2Index}-${level3Index}`}`,
                  level: 3,
                  text: level3Text,
                  maskable: true,
                });
              }
            });

            if (level2Index < level2List.length - 1) {
              lines.push({
                key: `sp2-${level1Index}-${level2Index}`,
                type: "spacer",
              });
            }
          });

          if (level1Index < source.treeNodes.length - 1) {
            lines.push({
              key: `sp1-${level1Index}`,
              type: "spacer",
            });
          }
        });
        return lines;
      }

      const fallbackText = this.normalizeLineText(ratingCriteria);
      if (!fallbackText) {
        return [];
      }
      return [
        {
          key: "fallback-0",
          level: 3,
          text: fallbackText,
          maskable: true,
        },
      ];
    },
    formatRatingContent(ratingCriteria) {
      const source = this.parseMaybeJsonObject(ratingCriteria);
      if (!source || !Array.isArray(source.treeNodes) || !source.treeNodes.length) {
        return "";
      }

      const lines = [];
      source.treeNodes.forEach((level1, level1Index) => {
        const level1Title = this.normalizeLineText(
          level1.primaryCategory || level1.secondaryCategory || level1.ratingItem
        );
        if (level1Title) {
          lines.push(level1Title);
        }

        const level2List = Array.isArray(level1.children) ? level1.children : [];
        level2List.forEach((level2, level2Index) => {
          const level2Title = this.normalizeLineText(
            level2.secondaryCategory || level2.primaryCategory || level2.ratingItem
          );
          if (level2Title) {
            lines.push(level2Title);
          }

          const level3List = Array.isArray(level2.children) ? level2.children : [];
          level3List.forEach((level3) => {
            const level3Item = this.normalizeLineText(level3.ratingItem);
            if (level3Item) {
              lines.push(level3Item);
            }
          });

          if (level2Index < level2List.length - 1) {
            lines.push("");
          }
        });

        if (level1Index < source.treeNodes.length - 1) {
          lines.push("");
        }
      });

      return lines.join("\n").replace(/\n{3,}/g, "\n\n").trim();
    },
    normalizeMediaUrl(url) {
      if (!url) {
        return require("../../assets/images/knowledge.png");
      }
      if (/^(https?:)?\/\//i.test(url)) {
        return encodeURI(url);
      }
      const host = String(this.mediaHost || "").replace(/\/$/, "");
      const normalizedPath = String(url).startsWith("/") ? url : `/${url}`;
      if (host.endsWith("/file") && normalizedPath.startsWith("/file/")) {
        return encodeURI(`${host}${normalizedPath.replace(/^\/file/, "")}`);
      }
      return encodeURI(`${host}${normalizedPath}`);
    },
    normalizeAudioUrl(url) {
      if (!url) {
        return "";
      }
      const toLowerMp3Ext = (value) =>
        String(value).replace(/\.mp3(?=([?#]|$))/i, ".mp3");
      if (/^(https?:)?\/\//i.test(url)) {
        return toLowerMp3Ext(encodeURI(url));
      }
      const host = String(this.mediaHost || "").replace(/\/$/, "");
      const normalizedPath = String(url).startsWith("/") ? url : `/${url}`;
      if (host.endsWith("/file") && normalizedPath.startsWith("/file/")) {
        return toLowerMp3Ext(
          encodeURI(`${host}${normalizedPath.replace(/^\/file/, "")}`)
        );
      }
      return toLowerMp3Ext(encodeURI(`${host}${normalizedPath}`));
    },
    normalizeVideoUrl(url) {
      if (!url) {
        return "";
      }
      if (/^(https?:)?\/\//i.test(url)) {
        return encodeURI(url);
      }
      const host = String(this.mediaHost || "").replace(/\/$/, "");
      const normalizedPath = String(url).startsWith("/") ? url : `/${url}`;
      if (host.endsWith("/file") && normalizedPath.startsWith("/file/")) {
        return encodeURI(`${host}${normalizedPath.replace(/^\/file/, "")}`);
      }
      return encodeURI(`${host}${normalizedPath}`);
    },
    getAudioUrlFromItem(item) {
      if (!item || typeof item !== "object") {
        return "";
      }
      const rawUrl =
        item.audioUrl ||
        item.audioPath ||
        item.voiceUrl ||
        item.voicePath ||
        item.soundUrl ||
        item.fileUrl ||
        "";
      return rawUrl ? this.normalizeAudioUrl(rawUrl) : "";
    },
    parseAudioDuration(value) {
      if (value === undefined || value === null || value === "") {
        return 0;
      }
      if (typeof value === "number" && Number.isFinite(value)) {
        if (value > 1000) {
          return Math.floor(value / 1000);
        }
        return Math.max(0, Math.floor(value));
      }
      const stringValue = String(value).trim();
      if (!stringValue) {
        return 0;
      }
      if (stringValue.includes(":")) {
        const timeParts = stringValue
          .split(":")
          .map((part) => Number(part))
          .filter((part) => Number.isFinite(part));
        if (timeParts.length === 3) {
          return Math.max(
            0,
            Math.floor(timeParts[0] * 3600 + timeParts[1] * 60 + timeParts[2])
          );
        }
        if (timeParts.length === 2) {
          return Math.max(0, Math.floor(timeParts[0] * 60 + timeParts[1]));
        }
      }
      const numberValue = Number(stringValue);
      if (Number.isFinite(numberValue)) {
        if (numberValue > 1000) {
          return Math.floor(numberValue / 1000);
        }
        return Math.max(0, Math.floor(numberValue));
      }
      return 0;
    },
    getAudioDurationFromItem(item) {
      if (!item || typeof item !== "object") {
        return 0;
      }
      return this.parseAudioDuration(
        item.audioTime || item.audioDuration || item.duration || item.voiceDuration || ""
      );
    },
    pauseAudio(resetTime = false) {
      const audio = this.$refs.audioPlayer;
      if (!audio) {
        this.isPlaying = false;
        return;
      }
      audio.pause();
      if (resetTime) {
        audio.currentTime = 0;
      }
      this.isPlaying = false;
    },
    syncAudioCurrentTime() {
      const audio = this.$refs.audioPlayer;
      if (!audio || !this.currentQuestion) {
        return;
      }
      const questionTime = Number(this.currentQuestion.currentTime || 0);
      if (Math.abs((audio.currentTime || 0) - questionTime) > 0.4) {
        audio.currentTime = questionTime;
      }
    },
    initFromRoute() {
      this.mediaHost = this.getMediaHostFromRoute();
      this.currentType = this.getCodeFromRoute();
      this.pageTitle =
        this.$route.query.categoryName ||
        this.$route.query.label ||
        this.pageTitle;
      this.activeTab = this.dynamicTabs[0] ? this.dynamicTabs[0].key : "part";
      this.practiceTime = 0;
      this.startPracticeTimer();
    },
    getRouteType() {
      return (
        this.toValidNumber(this.$route.query.type) ??
        this.toValidNumber(this.$route.params.type)
      );
    },
    shouldTrackPractice() {
      return this.getRouteType() !== 1;
    },
    startPracticeTimer() {
      this.stopPracticeTimer();
      if (!this.shouldTrackPractice()) {
        return;
      }
      this.practiceTimer = window.setInterval(() => {
        this.practiceTime += 1;
      }, 1000);
    },
    stopPracticeTimer() {
      if (this.practiceTimer) {
        window.clearInterval(this.practiceTimer);
        this.practiceTimer = null;
      }
    },
    async getKnowledgeListData() {
      const categoryId = this.getCategoryIdFromRoute();
      const userId = this.getGlobalUserId();
      if (!categoryId || !userId) {
        return;
      }

      const { data } = await knowledgeList({
        categoryId,
        // isFavorite,
        // isMastered,
        userId,
      });
      const sourceList = Array.isArray(data) ? data : [];
      this.questionList = sourceList.map((item, idx) => {
        const audioUrl = this.getAudioUrlFromItem(item);
        const duration = this.getAudioDurationFromItem(item);
        return {
        id: item.medicalKnowledgeId || item.categoryId || idx,
        medicalKnowledgeId: item.medicalKnowledgeId || null,
        categoryId: item.categoryId || categoryId,
        title: item.name || item.categoryName || "听诊题目",
        imageFront: this.normalizeMediaUrl(item.imageUrl),
        imageBack: this.normalizeMediaUrl(item.imageUrl),
        audioUrl,
        duration,
        currentTime: 0,
        part: item.part || "",
        mechanism: item.mechanism || "",
        feature: item.characteristics || "",
        disease: item.commonDiseases || "",
        formula: item.diagnosticFormula || "",
        ecgFeature: item.characteristics || "",
        mnemonic: item.mnemonic || "",
        briefHistory: item.briefMedicalHistory || "",
        videoTutorialImageUrl: item.videoTutorialImageUrl
          ? this.normalizeMediaUrl(item.videoTutorialImageUrl)
          : "",
        videoTutorialVideoUrl: item.videoTutorialVideoUrl
          ? this.normalizeVideoUrl(item.videoTutorialVideoUrl)
          : "",
        ratingLines: this.buildRatingLines(item.ratingCriteria),
        rating:
          this.formatRatingContent(item.ratingCriteria) ||
          item.ratingCriteriaName ||
          (typeof item.ratingCriteria === "string" ? item.ratingCriteria : "") ||
          "",
        isFavorite: Number(item.isFavorite) === 1,
        isMastered: Number(item.isMastered) === 1,
      }});

      const routeMedicalKnowledgeId = this.getMedicalKnowledgeIdFromRoute();
      if (routeMedicalKnowledgeId) {
        const targetIndex = this.questionList.findIndex(
          (item) =>
            String(item.medicalKnowledgeId || item.id) ===
            String(routeMedicalKnowledgeId)
        );
        if (targetIndex >= 0) {
          this.currentIndex = targetIndex;
        }
      }

      if (!this.$route.query.categoryName && sourceList.length) {
        this.pageTitle = sourceList[0].categoryName || this.pageTitle;
      }

      if (!this.dynamicTabs.some((tab) => tab.key === this.activeTab)) {
        this.activeTab = this.dynamicTabs[0] ? this.dynamicTabs[0].key : "part";
      }

      this.pauseAudio(true);
    },
    onTouchStart(event) {
      this.touchStartX = event.changedTouches[0].clientX;
    },
    onTouchEnd(event) {
      const endX = event.changedTouches[0].clientX;
      const distance = endX - this.touchStartX;
      if (Math.abs(distance) < 40) {
        return;
      }
      if (distance < 0) {
        this.toNext();
        return;
      }
      this.toPrev();
    },
    toNext() {
      if (this.currentIndex >= this.questionList.length - 1) {
        return;
      }
      this.pauseAudio(true);
      this.currentIndex += 1;
      this.activeTab = this.dynamicTabs[0] ? this.dynamicTabs[0].key : "part";
    },
    toPrev() {
      if (this.currentIndex <= 0) {
        return;
      }
      this.pauseAudio(true);
      this.currentIndex -= 1;
      this.activeTab = this.dynamicTabs[0] ? this.dynamicTabs[0].key : "part";
    },
    setProgressByClientX(clientX) {
      const track = this.$refs.progressTrack;
      if (!track || !this.currentQuestion) {
        return;
      }
      const rect = track.getBoundingClientRect();
      const safeClientX = Math.min(Math.max(clientX, rect.left), rect.right);
      const ratio = rect.width ? (safeClientX - rect.left) / rect.width : 0;
      const duration = Number(this.currentQuestion.duration || 0);
      this.currentQuestion.currentTime = Math.round(duration * ratio);
      this.syncAudioCurrentTime();
    },
    onTrackClick(event) {
      this.setProgressByClientX(event.clientX);
    },
    onDragStart() {
      this.isDraggingProgress = true;
    },
    onDragMove(event) {
      if (!this.isDraggingProgress) {
        return;
      }
      if (event.cancelable) {
        event.preventDefault();
      }
      const clientX = event.touches ? event.touches[0].clientX : event.clientX;
      this.setProgressByClientX(clientX);
    },
    onDragEnd() {
      this.isDraggingProgress = false;
    },
    async togglePlay() {
      if (!this.showMediaSection) {
        return;
      }
      const audio = this.$refs.audioPlayer;
      if (!audio || !this.currentQuestion.audioUrl) {
        return;
      }
      if (this.isPlaying) {
        this.pauseAudio(false);
        return;
      }
      this.syncAudioCurrentTime();
      try {
        await audio.play();
        this.isPlaying = true;
      } catch (error) {
        this.isPlaying = false;
        console.error("audio play failed", {
          src: this.currentQuestion.audioUrl,
          error,
        });
      }
    },
    onAudioCanPlay(event) {
      const duration = Math.floor(event.target.duration || 0);
      if (duration > 0 && !this.currentQuestion.duration) {
        this.currentQuestion.duration = duration;
      }
    },
    onAudioLoadedMetadata(event) {
      const duration = Math.floor(event.target.duration || 0);
      if (duration > 0) {
        this.currentQuestion.duration = duration;
      }
    },
    onAudioError(event) {
      this.isPlaying = false;
      const audio = event && event.target;
      const mediaError = audio && audio.error;
      console.error("audio element error", {
        src: this.currentQuestion.audioUrl,
        code: mediaError ? mediaError.code : null,
      });
    },
    onAudioTimeUpdate(event) {
      if (this.isDraggingProgress) {
        return;
      }
      this.currentQuestion.currentTime = Math.floor(event.target.currentTime || 0);
    },
    onAudioEnded() {
      const audio = this.$refs.audioPlayer;
      if (audio) {
        audio.currentTime = 0;
      }
      this.isPlaying = false;
      this.currentQuestion.currentTime = 0;
    },
    formatTime(totalSeconds) {
      const safe = Number(totalSeconds || 0);
      const hours = String(Math.floor(safe / 3600)).padStart(2, "0");
      const minutes = String(Math.floor((safe % 3600) / 60)).padStart(2, "0");
      const seconds = String(safe % 60).padStart(2, "0");
      return `${hours}:${minutes}:${seconds}`;
    },
    getCurrentQuestionKey() {
      const current = this.currentQuestion || {};
      return String(current.medicalKnowledgeId || current.id || this.currentIndex);
    },
    isRatingLineMaskable(line) {
      if (!line || line.type === "spacer") {
        return false;
      }
      return Boolean(line.maskable);
    },
    isRatingLineRevealed(line) {
      if (!this.isRatingLineMaskable(line)) {
        return true;
      }
      const questionKey = this.getCurrentQuestionKey();
      const questionRevealMap = this.ratingRevealMap[questionKey] || {};
      return Boolean(questionRevealMap[line.key]);
    },
    toggleRatingLine(line) {
      if (this.activeTab !== "rating" || !this.isRatingLineMaskable(line)) {
        return;
      }
      const questionKey = this.getCurrentQuestionKey();
      const questionRevealMap = this.ratingRevealMap[questionKey] || {};
      this.$set(this.ratingRevealMap, questionKey, {
        ...questionRevealMap,
        [line.key]: !questionRevealMap[line.key],
      });
    },
    async goBack() {
      const routeType = this.getRouteType();
      if (routeType === 1) {
        this.$router.go(-1);
        return;
      }
      if (this.isSubmittingLearnRecord) {
        return;
      }
      this.stopPracticeTimer();
      const medicalKnowledgeId =
        this.currentQuestion.medicalKnowledgeId || this.currentQuestion.id;
      const practiceTime = Math.max(0, Math.floor(Number(this.practiceTime || 0)));
      this.isSubmittingLearnRecord = true;
      try {
        if (medicalKnowledgeId) {
          await learnRecordSave(medicalKnowledgeId, practiceTime);
        }
      } catch (error) {
      } finally {
        this.isSubmittingLearnRecord = false;
        this.$router.go(-1);
      }
    },
    buildFavoriteSavePayload(overrideData = {}) {
      const current = this.currentQuestion || {};
      return {
        isFavorite: current.isFavorite ? 1 : 0,
        isMastered: current.isMastered ? 1 : 0,
        medicalKnowledgeId: current.medicalKnowledgeId || current.id,
        ...overrideData,
      };
    },
    showActionToast(text) {
      if (!text) {
        return;
      }
      this.actionToastText = text;
      this.actionToastVisible = true;
      if (this.actionToastTimer) {
        window.clearTimeout(this.actionToastTimer);
      }
      this.actionToastTimer = window.setTimeout(() => {
        this.actionToastVisible = false;
        this.actionToastTimer = null;
      }, 1400);
    },
    async toggleFavorite() {
      if (!this.questionList.length || this.isSubmittingFavorite) {
        return;
      }
      const current = this.currentQuestion;
      const previous = Boolean(current.isFavorite);
      const nextValue = !previous;
      this.isSubmittingFavorite = true;
      current.isFavorite = nextValue;
      try {
        await favoriteSave(
          this.buildFavoriteSavePayload({ isFavorite: nextValue ? 1 : 0 })
        );
        this.showActionToast(nextValue ? "已收藏" : "取消收藏");
      } catch (error) {
        current.isFavorite = previous;
      } finally {
        this.isSubmittingFavorite = false;
      }
    },
    async toggleMastered() {
      if (!this.questionList.length || this.isSubmittingMastered) {
        return;
      }
      const current = this.currentQuestion;
      const previous = Boolean(current.isMastered);
      const nextValue = !previous;
      this.isSubmittingMastered = true;
      current.isMastered = nextValue;
      try {
        await favoriteSave(
          this.buildFavoriteSavePayload({ isMastered: nextValue ? 1 : 0 })
        );
        this.showActionToast(nextValue ? "已掌握" : "取消掌握");
      } catch (error) {
        current.isMastered = previous;
      } finally {
        this.isSubmittingMastered = false;
      }
    },
    openPointList() {
      this.showPointList = true;
    },
    closePointList() {
      this.showPointList = false;
    },
    selectPoint(index) {
      this.pauseAudio(true);
      this.currentIndex = index;
      this.activeTab = this.dynamicTabs[0] ? this.dynamicTabs[0].key : "part";
      this.closePointList();
    },
    isMastered(item, index) {
      if (typeof item.isMastered === "boolean") {
        return item.isMastered;
      }
      return index === this.currentIndex;
    },
    handleKnowledgeHomeClick() {
      if (!this.knowledgePointJumpUrl) {
        return;
      }
      window.location.href = this.knowledgePointJumpUrl;
    },
  },
};
</script>

<style lang="scss" scoped>
.knowledgeDetailsPage {
  min-height: 100vh;
  padding-bottom: 90px;
  background: #f4f8fb;

  .titleBox {
    padding: 44px 16px 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 92px;

    .titleLeft {
      flex: 0 0 20px;
      width: 20px;
      height: 20px;
      display: flex;
      align-items: center;
      justify-content: center;
      img {
        width: 20px;
        height: 20px;
      }
    }

    .titleRight {
      flex: 0 0 20px;
      width: 20px;
      height: 20px;
    }

    .titleCentre {
      flex: 1;
      text-align: center;
      font-family: PingFangSC, PingFang SC;
      font-weight: 600;
      font-size: 16px;
      line-height: 20px;
      color: #2f3445;
      white-space: nowrap;
    }
  }

  .contentCard {
    margin: 12px 12px 0;
    background: #ffffff;
    height: 388px;
    border-radius: 12px;
    padding: 16px;

    &.noMedia {
      height: auto;
      min-height: 88px;
      padding-bottom: 14px;
    }

    .questionHeader {
      display: flex;
      justify-content: center;
      align-items: center;
      position: relative;

      .questionTitle {
        font-family: PingFangSC, PingFang SC;
        font-size: 18px;
        color: #2f2f46;
        font-weight: 500;
      }

      .questionCount {
        position: absolute;
        right: 4px;
        top: 0;
        width: 59px;
        height: 24px;
        text-align: center;
        border-radius: 12px;
        font-family: PingFangSC, PingFang SC;
        font-size: 14px;
        color: #8f91a1;
        background: #f0f1f6;
      }
    }

    .imageWrap {
      margin-top: 24px;
      width: 100%;
      height: 180px;

      img {
        width: 100%;
        height: 100%;
      }
    }

    .audioBox {
      margin-top: 34px;

      .timeRow {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;

        span {
          font-family: PingFangSC, PingFang SC;
          font-size: 12px;
          color: #9ba2b1;
        }

        .progressRange {
          flex: 1;
          height: 22px;
          position: relative;
          display: flex;
          align-items: center;

          .progressRangeBg {
            width: 100%;
            height: 6px;
            border-radius: 3px;
            background: #e8ebf2;
          }

          .progressRangeActive {
            position: absolute;
            left: 0;
            top: 50%;
            transform: translateY(-50%);
            height: 6px;
            border-radius: 3px;
            background: #3a78f2;
          }

          .progressCircle {
            position: absolute;
            top: 50%;
            transform: translateY(-50%);
            width: 18px;
            height: 18px;
          }
        }
      }

      .waveBox {
        margin-top: 14px;
        height: 56px;
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;

        .waveImg {
          width: 230px;
          height: 30px;
          object-fit: contain;
        }

        .playBtn {
          position: absolute;
          width: 48px;
          height: 48px;
          z-index: 2;
        }
      }
    }
  }

  .bannerBox {
    padding: 10px 15px;
    width: 100%;

    img {
      width: 100%;
      height: 100px;
    }
  }

  .swipeArea {
    width: 100%;
  }

  .tabBox {
    margin: 12px 16px 0;
    border-radius: 12px 12px 0 0;
    padding: 0 20px;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 14px;

    &.fullTabs {
      justify-content: space-between;
      gap: 0;
    }

    .tabItem {
      height: 44px;
      line-height: 44px;
      font-family: PingFangSC, PingFang SC;
      font-size: 16px;
      color: #697083;
      position: relative;
    }

    .tabItem.active {
      color: #30374a;
      font-weight: 600;
    }

    .tabItem.active::after {
      content: "";
      position: absolute;
      left: 50%;
      transform: translateX(-50%);
      bottom: 0;
      width: 18px;
      height: 4px;
      border-radius: 2px;
      background: #3a78f2;
    }
  }

  .descCard {
    margin: 0 16px;
    min-height: 150px;
    background: #ffffff;
    border-radius: 12px;
    padding: 16px;
    font-family: PingFangSC, PingFang SC;
    font-size: 15px;
    line-height: 28px;
    color: #747c8f;
    white-space: pre-wrap;
  }

  .ratingCard {
    .ratingLine {
      width: 100%;

      &.spacer {
        height: 16px;
      }

      .maskBlock {
        display: block;
        width: 100%;
        border-radius: 2px;
        background: #AECAE9;
      }

      &.level1 {
        color: #2F2F46;
        font-size: 16px;
        font-weight: 500;
        line-height: 1.35;
        margin-top: 2px;

        .maskBlock {
          height: 22px;
        }
      }

      &.level2 {
        color: #2F2F46;
        font-size: 14px;
        font-weight:500;
        line-height: 1.4;
        margin-top: 8px;

        .maskBlock {
          height: 20px;
        }
      }

      &.level3 {
        color: #50536B;
        font-size: 14px;
        font-weight: 400;
        line-height: 1.55;
        margin-top: 8px;

        .maskBlock {
          height: 24px;
        }
      }

      &.masked {
        cursor: pointer;
      }

      &.maskableLine {
        color: #50536B;
        font-size: 14px;
      }
    }
  }

  .videoTutorialCard {
    .videoTutorialPlayer {
      width: 100%;
      border-radius: 8px;
      background: #000;
      display: block;
    }

    .videoTutorialEmpty {
      color: #8d93a1;
      font-size: 14px;
      text-align: center;
      line-height: 120px;
    }
  }

  .bottomBar {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    height: 84px;
    background: #ffffff;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 16px 12px;
    box-sizing: border-box;

    .masteredBtn {
      width: 168px;
      height: 48px;
      border: none;
      border-radius: 24px;
      background: #3a78f2;
      color: #ffffff;
      font-family: PingFangSC, PingFang SC;
      font-size: 19px;
      font-weight: 500;
      line-height: 48px;
      text-align: center;
      margin-left: 0;
      flex-shrink: 0;
      box-shadow: 0 6px 14px rgba(58, 120, 242, 0.25);
    }

    .actionList {
      flex: 1;
      display: flex;
      justify-content: flex-end;
      gap: 22px;

      .actionItem {
        display: flex;
        flex-direction: column;
        align-items: center;

        img {
          width: 34px;
          height: 34px;
        }

        span {
          margin-top: 4px;
          font-family: PingFangSC, PingFang SC;
          font-size: 12px;
          color: #8d93a1;
        }
      }
    }
  }

  .pointMask {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.35);
    z-index: 1000;
    display: flex;
    align-items: flex-end;

    .pointSheet {
      width: 100%;
      max-height: 72vh;
      background: #ffffff;
      border-radius: 20px 20px 0 0;
      padding: 18px 16px 20px;
      box-sizing: border-box;
      overflow: auto;
    }

    .pointLegend {
      display: flex;
      align-items: center;
      gap: 24px;
      padding-bottom: 12px;

      .legendItem {
        display: flex;
        align-items: center;
        gap: 10px;
        font-family: PingFangSC, PingFang SC;
        font-size: 15px;
        color: #6d7385;
      }
    }

    .dot {
      width: 12px;
      height: 12px;
      border-radius: 50%;

      &.mastered {
        background: #3a78f2;
      }

      &.unMastered {
        background: #e3e6ef;
      }
    }

    .pointList {
      .pointRow {
        height: 66px;
        border-top: 1px solid #eff1f6;
        display: flex;
        align-items: center;
        justify-content: space-between;

        .leftInfo {
          display: flex;
          align-items: center;
          gap: 12px;
          min-width: 0;
        }

        .rowTitle {
          font-family: PingFangSC, PingFang SC;
          font-size: 34px;
          transform: scale(0.5);
          transform-origin: left center;
          color: #5d6377;
          white-space: nowrap;
          max-width: 520px;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .rowArrow {
          width: 18px;
          height: 18px;
          opacity: 0.6;
        }
      }

      .pointRow:last-child {
        border-bottom: 1px solid #eff1f6;
      }
    }
  }

  .actionToast {
    position: fixed;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    min-width: 96px;
    max-width: 260px;
    padding: 9px 14px;
    background: rgba(51, 51, 51, 0.8);
    border-radius: 18px;
    font-family: PingFangSC, PingFang SC;
    font-weight: 400;
    font-size: 13px;
    color: #FFFFFF;
    line-height: 18px;
    text-align: center;
    font-style: normal;
    font-family: PingFangSC, PingFang SC;
    z-index: 1300;
    pointer-events: none;
  }

  .action-toast-fade-enter-active,
  .action-toast-fade-leave-active {
    transition: opacity 0.22s ease;
  }

  .action-toast-fade-enter,
  .action-toast-fade-leave-to {
    opacity: 0;
  }
}
</style>
